<template>
  <v-container fluid class="pa-0 ma-2">
    <v-row class="align-center mb-4" style="background-color: #d3ffd3;">
      <v-col class="d-flex align-center" cols="12">
        <img :src="require('@/assets/n_6@2x.png')" alt="Logo" style="height: 50px; margin-right: 10px;">
        <h1 style="margin: 0;">営業トーク例作成</h1>
      </v-col>
    </v-row>
    <v-row class="mb-4">
      <v-col cols="12">
        <h2>プレゼン相手は誰ですか？</h2>
      </v-col>
    </v-row>
    <v-row class="mb-4 ml-4"
      v-for="(option, index) in options"
      :key="index">
      <v-btn
        outlined
        @click="startSSE(option)"
        :disabled="connectionStatus !== '準備完了'"
      >
        {{ option }}
      </v-btn>
      <p class="d-flex align-center justify-center">
        {{ buttonDescription[index] ? buttonDescription[index] : '' }}
      </p>
    </v-row>
    <v-divider class="my-4"></v-divider>
    <v-row class="pr-4">
      <v-col cols="12">
        <h3>[営業トーク例] <small>※この営業トークは生成AIを使用して作成しています。こちらの営業トークはSEJの真意ではありません。営業トーク例が作成途中でエラーが発生した場合、再度上部のプレゼン相手選択のボタンを押下してください。同様の条件で営業トークを再作成します。</small></h3>
        <div v-if="dialogMessage" class="error-message">
          {{ dialogMessage }}
        </div>
        <p class="talk-text">{{ talkExample }}</p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { mdiEmail, mdiContentCopy } from "@mdi/js";
import { errorMessages } from '@/constants/errorMessages.js';
import { fetchArticleRegionCategory, fetchArticleIndustryCategory } from "@/api";
import { inject } from 'vue';

let gtm = null;
let regions = null;
let industries = null;

export default {
  setup() {
    const route = useRoute();
    const articleId = route.params.article_id;
    const options = ref(["初回", "複数回営業", "契約締結直前"]);
    const buttonDescription = ref([
      "・・・初めて訪問するとき、相手とお会いできた時", 
      "・・・交渉中の相手がいる時に向け", 
      "・・・相手方と信頼関係が構築された方"
    ]);
    const talkExample = ref(``);
    const connectionStatus = ref('準備完了'); // SSE接続状態を管理
    const dialogMessage = ref('');
    const timeoutDuration = 60000; // 60秒
    const baseUrl = process.env.VUE_APP_API_URL;
    const talkExampleApiUrl = baseUrl + "/api/v1/stream/talk_example";

    let timeoutId = null;
    let eventSource = null;

    function startSSE(customerRelationship) {
      // Google Tag Manager に情報を送信
      gtm?.sendAiTalkRegionCategory(regions, customerRelationship)
      gtm?.sendAiTalkIndustryCategory(industries, customerRelationship)

      dialogMessage.value = '';
      connectionStatus.value = '接続中...';
      // 既存のトーク例をクリア
      talkExample.value = '';

      // 既存のEventSourceがあれば閉じる
      if (eventSource) {
        eventSource.close();
      }

      // 事前にステータスコードを確認
      fetch(`${talkExampleApiUrl}?article_id=${articleId}&customer_relationship=${customerRelationship}`)
        .then((response) => {

          if (response.status !== 200) {
            dialogMessage.value = errorMessages.e01401("E00402", response.status);
            return;
          }

          // EventSourceを作成
          eventSource = new EventSource(`${talkExampleApiUrl}?article_id=${articleId}&customer_relationship=${customerRelationship}`);

          // メッセージを受信したときの処理
          eventSource.onmessage = function (event) {
            connectionStatus.value = '受信中';
            // タイムアウトをリセット
            clearTimeout(timeoutId);
            
            // 受信したデータがエラーか判定
            if (event.data.startsWith("ERROR:")) {
              // エラーメッセージ例: "ERROR:E00404:申し訳ありませんが..."
              // コロンで分割してエラーコードとメッセージを取得
              const parts = event.data.split(":");
              // parts[0] = "ERROR", parts[1] = エラーコード, parts[2] = エラーメッセージ...
              // エラーメッセージ全体を再構築（コロン以降まとめる）
              const errorCode = parts[1];
              const errorMsg = parts.slice(2).join(":"); // エラーメッセージ部分を再結合
              dialogMessage.value = `${errorCode}: ${errorMsg}`;
            } else {
              // 通常のテキストの場合はtalkExampleに追加
              talkExample.value += event.data;
            }

            // タイムアウトを再設定
            timeoutId = setTimeout(function () {
              // タイムアウト時の処理
              eventSource.close();
              connectionStatus.value = "タイムアウト";
            }, timeoutDuration);
          };

          // 接続が開かれたときの処理
          eventSource.onopen = function () {
            // 連打防止 & 生成中であることを示す
            connectionStatus.value = '準備完了';
          };

          // エラーが発生したときの処理
          eventSource.onerror = function () {
            connectionStatus.value = '準備完了';
            // タイムアウトをクリア
            clearTimeout(timeoutId);
            // 接続を閉じる
            eventSource.close();
          };
        })
        .catch(error => {
          console.error('Fetchエラー:', error);
          dialogMessage.value = errorMessages.e01401("E00402", connectionStatus.value);
          connectionStatus.value = '準備完了';
        });
    }

    onMounted(async () => {
      gtm = inject('$gtm');

      const regionsResponse = await fetchArticleRegionCategory(articleId);
      regions = regionsResponse.data?.regions || [];  // regions配列を取得
      const industriesResponse = await fetchArticleIndustryCategory(articleId);
      industries = industriesResponse.data?.industries || [];  // industries配列を取得
    });

    return {
      options,
      talkExample,
      mdiEmail,
      mdiContentCopy,
      startSSE,
      dialogMessage,
      connectionStatus,
      buttonDescription,
    };
  },
};
</script>

<style scoped>
.v-btn {
  margin: 5px;
}

.talk-text {
  white-space: normal;
  overflow-wrap: break-word;
  /* 必要に応じて単語単位で折り返し */
}

.error-message {
  color: red;
  text-align: center;
  margin: 10px 0;
  padding: 10px;
  font-weight: bold;
}
</style>
